import styled from 'styled-components';

import ReportResultLayout from 'dashboard/components/ReportResult/ReportResultLayout';
import Tooltip from 'dashboard/components/Tooltip';
import TableLayout, {
  EmbeddedTableNullState,
  TableTD,
  TableTH,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import { formatCompanyPosition, nameCase } from 'dashboard/lib/utils/stringFormat';
import { AssociatedPerson, UsSecretaryOfStateRecord } from 'dashboard/models/registry-record';
import { ReportBusinessAssociatedPersons } from 'dashboard/models/report/business-associated-persons';
import Card from 'design-system/components/Card';
import Sticker from 'design-system/components/Sticker';
import { Paragraph } from 'design-system/components/Text';
import * as Spacing from 'design-system/variables/spacing';
import { US_STATES } from 'lib/constants/locales';

interface BusinessAssociatedPersonsProps {
  report: ReportBusinessAssociatedPersons;
  isRedacted: boolean;
  multiCardFormat: boolean;
}

export default ({ report, multiCardFormat = false }: BusinessAssociatedPersonsProps) => {
  return (
    <ReportResultLayout multiCardFormat={multiCardFormat}>
      <AssociatedPersons report={report} />
    </ReportResultLayout>
  );
};

const StickersCellRegistryRecords = styled.div`
  display: block;
  justify-content: flex-start;
  flex-flow: wrap;
`;

const StickersCellPositions = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-flow: wrap;
`;

const StickerContainer = styled.div`
  margin: 2px;
`;

const TextWithHelperIcon = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start !important;
`;

const PaddedTooltip = styled(Tooltip)`
  margin-bottom: 8px;
  padding: 8px;
`;

const TooltipTitle = styled(Paragraph)`
  font-weight: 500;
  padding-top: 4px;
`;

const TH45Width = styled(TableTH)`
  width: 45%;
`;

const TH10Width = styled(TableTH)`
  width: 10%;
`;

const AssociatedPersons = ({ report }: { report: ReportBusinessAssociatedPersons }) => {
  const { 'associated-persons': associatedPersons } = report.attributes;

  return (
    <>
      <Card.Title>Associated Persons</Card.Title>
      <TableLayout
        dataTest='associated-people-table'
        showHeader={true}
        isEmpty={(associatedPersons ?? []).length === 0}
        nullState={
          <EmbeddedTableNullState
            marginVertical={Spacing.BASE * 2}
            dataTestBlock='associated-people-table'
          >
            No associated people found
          </EmbeddedTableNullState>
        }
        headerTR={
          <>
            <TH45Width>Name</TH45Width>
            <TH10Width>
              <TextWithHelperIcon>Registry Records</TextWithHelperIcon>
            </TH10Width>
            <TH45Width>Positions</TH45Width>
          </>
        }
      >
        {(associatedPersons ?? []).map((associatedPerson: AssociatedPerson) => {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          const usSoSRecords = associatedPerson['registry-records']
            .filter((r) => r.type === 'us_secretary_of_state')
            .map((r) => r as UsSecretaryOfStateRecord);
          const activeRecords = usSoSRecords.filter(
            (r) => r.status.standardized?.toLowerCase() === 'active',
          );
          const inactiveRecords = usSoSRecords.filter(
            (r) => r.status.standardized?.toLowerCase() === 'inactive',
          );
          const unknownRecords = usSoSRecords.filter(
            (r) =>
              !['active', 'inactive'].includes(r.status.standardized?.toLowerCase() || 'unknown'),
          );
          return (
            <TableTR key={associatedPerson.names[0].value}>
              <TableTD>{nameCase(associatedPerson.names[0].value)}</TableTD>
              <TableTD>
                <PaddedTooltip
                  trigger='hover'
                  tooltip={
                    <>
                      {activeRecords.length > 0 && (
                        <TooltipTitle size='sm' color='primary'>
                          &nbsp;Active Status
                        </TooltipTitle>
                      )}
                      {activeRecords.map((record) => {
                        return (
                          <StickerContainer key='tooltip_active_status'>
                            <Sticker variant='green'>
                              {US_STATES[record['registration-state'] as keyof typeof US_STATES]}{' '}
                              SoS
                            </Sticker>
                          </StickerContainer>
                        );
                      })}
                      {inactiveRecords.length > 0 && (
                        <>
                          <TooltipTitle size='sm' color='primary'>
                            &nbsp;Inactive Status
                          </TooltipTitle>
                        </>
                      )}
                      {inactiveRecords.map((record) => {
                        return (
                          <StickerContainer key='tooltip_inactive_status'>
                            <Sticker variant='red'>
                              {US_STATES[record['registration-state'] as keyof typeof US_STATES]}{' '}
                              SoS
                            </Sticker>
                          </StickerContainer>
                        );
                      })}
                      {unknownRecords.length > 0 && (
                        <>
                          <TooltipTitle size='sm' color='primary'>
                            &nbsp;Unknown Status
                          </TooltipTitle>
                        </>
                      )}
                      {unknownRecords.map((record) => {
                        return (
                          <StickerContainer key='tooltip_unknown_status'>
                            <Sticker variant='grey'>
                              {US_STATES[record['registration-state'] as keyof typeof US_STATES]}{' '}
                              SoS
                            </Sticker>
                          </StickerContainer>
                        );
                      })}
                    </>
                  }
                  hideArrow={true}
                  placement='top'
                >
                  <StickersCellRegistryRecords>
                    {(activeRecords ?? []).length > 0 ? (
                      <StickerContainer key='active_status'>
                        <Sticker variant='teal'>{activeRecords.length} Active</Sticker>
                      </StickerContainer>
                    ) : (
                      <></>
                    )}
                    {(inactiveRecords ?? []).length > 0 ? (
                      <StickerContainer key='inactive_status'>
                        <Sticker variant='yellow'>{inactiveRecords.length} Inactive</Sticker>
                      </StickerContainer>
                    ) : (
                      <></>
                    )}
                    {(unknownRecords ?? []).length > 0 ? (
                      <StickerContainer key='unknown_status'>
                        <Sticker variant='grey'>{unknownRecords.length} Unknown</Sticker>
                      </StickerContainer>
                    ) : (
                      <></>
                    )}
                  </StickersCellRegistryRecords>
                </PaddedTooltip>
              </TableTD>
              <TableTD>
                <StickersCellPositions>
                  {(associatedPerson.positions ?? []).length > 0 ? (
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    associatedPerson.positions.map((title) => (
                      <StickerContainer key={title.value}>
                        <Sticker>{formatCompanyPosition(title.value)}</Sticker>
                      </StickerContainer>
                    ))
                  ) : (
                    <StickerContainer key='Not determined'>
                      <Sticker>{formatCompanyPosition('Not determined')}</Sticker>
                    </StickerContainer>
                  )}
                </StickersCellPositions>
              </TableTD>
            </TableTR>
          );
        })}
      </TableLayout>
    </>
  );
};
